/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import { firebase } from "@/utilities/firebase";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: "/",
          redirect: "/dashboard"
        },
        {
          path: "/hospitalreports",
          name: "hospitalreports",
          component: () =>
            import("@/modules/hospitalReport/views/HospitalReportListPage"),
          meta: {
            pageTitle: "Hospital Report List",
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "HospitalReport List", active: true }
            ],
            requiresAuth: true
          }
        },
        {
          path: "/ventilators",
          name: "ventilators",
          component: () =>
            import("@/modules/ventilator/views/VentilatorListPage"),
          meta: {
            pageTitle: "HospitalReport List",
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "HospitalReport List", active: true }
            ],
            requiresAuth: true
          }
        },
        {
          path: "/ventilator/:id",
          name: "ventilatorView",
          component: () => import("@/modules/ventilator/views/VentilatorView"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "HospitalReport List", url: "/" },
              { title: "Ventilator", active: true }
            ],
            requiresAuth: true
          }
        },
        {
          path: "/servicerequests",
          name: "servicerequests",
          component: () =>
            import("@/modules/service-request/views/ServiceRequestListPage"),
          meta: {
            pageTitle: "Service Request List",
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Service Request List", active: true }
            ],
            requiresAuth: true
          }
        },
        {
          path: "/servicerequest/:id",
          name: "serviceRequestView",
          component: () =>
            import("@/modules/service-request/views/ServiceRequestView"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Service Request List", url: "/servicerequests" },
              { title: "Service Request", active: true }
            ],
            requiresAuth: true
          }
        },
        {
          path: "/settings",
          name: "Setting List",
          component: () => import("@/modules/setting/views/SettingListPage"),
          meta: {
            pageTitle: "Setting List",
            requiresAuth: true
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "/login",
          name: "login",
          component: () => import("@/views/pages/Login.vue"),
          meta: {
            requiresGuest: true
          }
        },
        {
          path: "/resetpassword",
          name: "resetpassword",
          component: () => import("@/views/pages/ResetPassword.vue"),
          meta: {
            requiresGuest: true
          }
        },
        {
          path: "/dashboard",
          name: "dashboard",
          component: () =>
            import("@/modules/dashboard/views/DashboardMainPage.vue")
        },
        {
          path: "/dashboards/global",
          name: "globalDashboard",
          component: () =>
            import("@/modules/dashboard/views/GlobalDashboard.vue")
        },
        {
          path: "/dashboards/australia",
          name: "australiaDashboard",
          component: () =>
            import("@/modules/dashboard/views/AustraliaDashboard.vue")
        },
        {
          path: "/dashboards/victoria",
          name: "victoriaDashboard",
          component: () =>
            import("@/modules/dashboard/views/VictorianDashboard.vue")
        },
        {
          path: "/dashboards/rssjournals/:journal",
          name: "RssJournalFeed",
          component: () =>
            import("@/modules/dashboard/views/RssJournalFeed.vue"),
          props: true
        },
        {
          path: "/dashboards/hospital/:id",
          name: "hospitalDashboard",
          component: () =>
            import("@/modules/dashboard/views/HospitalDashboard.vue")
        },
        {
          path: "/pages/error-404",
          name: "page-error-404",
          component: () => import("@/views/pages/Error404.vue")
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/page-error-404"
    }
  ]
});

router.beforeEach((to, from, next) => {
  let currentUser = firebase.auth().currentUser;

  if (to.meta.requiresAuth) {
    if (currentUser) {
      next();
    } else {
      next({ name: "login" });
    }
  } else if (to.meta.requiresGuest) {
    if (!currentUser) {
      next();
    } else {
      next({ name: "ventilators" });
    }
  } else {
    next();
  }
});
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
